import FullCalendar, {
  CustomContentGenerator,
  EventApi,
  EventClickArg,
  EventContentArg,
  MountArg,
} from '@fullcalendar/react';
import interactionPlugin from '@fullcalendar/interaction';
import timeGridPlugin from '@fullcalendar/timegrid';
import {
  Event,
  TimeZone,
  Filters,
  SelectedLocation,
  Opening,
  FlexCalSummary,
  ExternalCalendarEventSummary,
  Reservation,
  ExternalCalendar,
} from '../../utils/types';
import { DateTime, Duration } from 'luxon';
import React, { useContext, useEffect } from 'react';
import { LoadingOrigin, ViewType } from '../../utils/enums';
import { ClockLoader } from 'react-spinners';
import { LoadingContext } from '../../utils/contexts/loadingContext';
import isBookedExternally from '../../utils/helpers/isBookedExternally';
import isBookedInternally from '../../utils/helpers/isBookedInternally';
import handleDST from '../../utils/helpers/handleDST';
import { UserProfile, useUser } from '@auth0/nextjs-auth0';
import styles from './Calendar.module.css';
import { getFlexStartAndEndDate } from '../../utils/helpers/getFlexStartAndEndDate';
import { useCalendarFilters } from '../../utils/hooks/useCalendarFilters';

type Props = {
  flexCal: FlexCalSummary;
  flexCalOpenings: Opening[] | undefined;
  externalEvents: ExternalCalendarEventSummary[] | undefined;
  reservations: Reservation[] | undefined;
  timeZone: TimeZone;
  filters: Filters;
  setFilters: React.Dispatch<React.SetStateAction<Filters>>;
  isEventClicked: boolean;
  setIsEventClicked: React.Dispatch<React.SetStateAction<boolean>>;
  clickedEvent: EventApi | undefined;
  setClickedEvent: React.Dispatch<React.SetStateAction<EventApi | undefined>>;
  selectedLocation: SelectedLocation;
  setCurrentView: React.Dispatch<React.SetStateAction<ViewType>>;
  calendarHeight: number | undefined;
  calendarWidth: number | undefined;
  participantEvents: ExternalCalendarEventSummary[] | undefined;
  externalCalendars: ExternalCalendar[] | undefined;
};

let earliestTimeSlot = 8;

const Calendar = React.forwardRef<FullCalendar, Props>((props, calendarRef) => {
  const { user } = useUser();
  const { loading } = useContext(LoadingContext);

  // Display loader
  if (
    props.flexCal === undefined ||
    (loading?.isloading &&
      loading.loadingOrigin === LoadingOrigin.fullCalendar) ||
    props.flexCalOpenings === undefined ||
    props.externalEvents === undefined ||
    props.reservations === undefined
  ) {
    return (
      <div className="flex flex-col items-center justify-center w-full h-full bg-confirmed-secondary">
        <ClockLoader color="#86E3A8" />
      </div>
    );
  }

  const handleEventClick = (event: EventClickArg) => {
    props.setClickedEvent(event.event);
    props.setIsEventClicked(true);
    props.setCurrentView(ViewType.Day);
  };

  const { start, end } = getFlexStartAndEndDate(props.flexCal);
  const { getFilters, getDurationInMinutes } = useCalendarFilters(
    props.filters
  );

  // Handle filter initialization separately
  useEffect(() => {
    if (!props.flexCalOpenings) return;

    let tempFilters = JSON.parse(JSON.stringify(props.filters));

    props.flexCalOpenings.forEach((opening) => {
      opening.durations?.forEach((duration) => {
        const durationAsMin = getDurationInMinutes(duration);
        tempFilters = getFilters(
          durationAsMin,
          opening.physicalLocations,
          opening.virtualLocations
        );
      });
    });

    if (JSON.stringify(tempFilters) !== JSON.stringify(props.filters)) {
      props.setFilters(tempFilters);
    }
  }, [props.flexCalOpenings]);

  return (
    <FullCalendar
      plugins={[timeGridPlugin, interactionPlugin]}
      editable={false}
      selectable
      nowIndicator={true}
      events={createTimeSlots(
        props.flexCal,
        props.flexCalOpenings,
        props.externalEvents,
        props.participantEvents,
        props.externalCalendars,
        props.reservations,
        props.filters,
        props.timeZone,
        props.clickedEvent,
        props.selectedLocation,
        user
      )}
      headerToolbar={{
        start: '',
        center: '',
        end: '',
      }}
      dayHeaders={true}
      businessHours={true}
      dayHeaderFormat={
        props.calendarWidth! <= 820
          ? { weekday: 'narrow', day: 'numeric' }
          : { weekday: 'long', day: 'numeric' }
      }
      initialView={'timeGridWeek'}
      height={props.calendarHeight === 240 ? '170px' : 'calc(100vh - 11em)'}
      allDaySlot={false}
      scrollTimeReset={false}
      validRange={{
        start: start.toString(),
        end: end.toString(),
      }}
      eventTimeFormat={{
        hour: 'numeric',
        minute: '2-digit',
        meridiem: false,
      }}
      displayEventEnd={false}
      slotEventOverlap={false}
      eventDisplay={'block'}
      eventMouseEnter={(mouseEnterInfo) => {
        if (mouseEnterInfo.event.extendedProps.type === 'subscriber') {
          mouseEnterInfo.el.style.backgroundColor = '#40978F';
          mouseEnterInfo.el.style.filter = 'drop-shadow(0 0 0.75rem #86E3A8)';
        }
      }}
      eventMouseLeave={(mouseExitInfo) => {
        if (mouseExitInfo.event.extendedProps.type === 'subscriber') {
          if (mouseExitInfo.event.id !== 'clicked') {
            mouseExitInfo.el.style.backgroundColor = '#FEFEFF';
          } else {
            mouseExitInfo.el.style.backgroundColor = '#86E3A8';
          }
          mouseExitInfo.el.style.filter = 'none';
        }
      }}
      eventClick={(arg) => {
        if (arg.event.extendedProps.type === 'subscriber') {
          handleEventClick(arg);
        }
      }}
      eventClassNames={(eventContentArg) => {
        if (eventContentArg.event.extendedProps.type === 'participant') {
          return `${styles.participantEvent}`;
        } else {
          return '';
        }
      }}
      eventDidMount={(
        arg: MountArg<EventContentArg>
      ): CustomContentGenerator<EventContentArg> => {
        const eventParent = arg.el.parentElement;
        const events = arg.view.calendar.getEvents();
        const overlapEvents = events.filter(
          (e) =>
            e.start &&
            e.end &&
            arg.event.start &&
            arg.event.end &&
            e.end > arg.event.start &&
            arg.event.end > e.start
        );

        let distinctDurations: number[] = [];
        overlapEvents.forEach((e) => {
          if (e.start && e.end) {
            const duration = DateTime.fromJSDate(e.end).diff(
              DateTime.fromJSDate(e.start),
              'minutes'
            );

            if (!distinctDurations.includes(duration.toMillis())) {
              if (e.extendedProps.type !== 'participant') {
                distinctDurations.push(duration.toMillis());
              } else if (!distinctDurations.includes(0)) {
                distinctDurations.push(0);
              }
            }
          }
        });

        let eventDuration = 0;
        if (
          arg.event.extendedProps.type !== 'participant' &&
          arg.event.end &&
          arg.event.start
        ) {
          eventDuration = DateTime.fromJSDate(arg.event.end)
            .diff(DateTime.fromJSDate(arg.event.start), 'minutes')
            .toMillis();
        }

        if (!distinctDurations.includes(eventDuration)) {
          distinctDurations.push(eventDuration);
        }
        distinctDurations = distinctDurations.sort((a, b) => b - a);

        if (eventParent) {
          if (arg.event.extendedProps.type === 'participant') {
            eventParent.classList.add(`${styles.participantEventContainer}`);
          } else if (distinctDurations.includes(0)) {
            if (distinctDurations.length === 1) {
              eventParent.classList.add(`${styles.none}`);
            } else if (distinctDurations.length === 2) {
              getSlotPositioning(eventDuration, distinctDurations, eventParent);
              eventParent.classList.add(`${styles.one}`);
            } else if (distinctDurations.length === 3) {
              getSlotPositioning(eventDuration, distinctDurations, eventParent);
              eventParent.classList.add(`${styles.two}`);
            } else if (distinctDurations.length === 4) {
              getSlotPositioning(eventDuration, distinctDurations, eventParent);
              eventParent.classList.add(`${styles.three}`);
            } else if (distinctDurations.length === 5) {
              getSlotPositioning(eventDuration, distinctDurations, eventParent);
              eventParent.classList.add(`${styles.four}`);
            } else if (distinctDurations.length === 6) {
              getSlotPositioning(eventDuration, distinctDurations, eventParent);
              eventParent.classList.add(`${styles.five}`);
            }
          }
        }
        return undefined;
      }}
      ref={calendarRef}
    />
  );
});

export default Calendar;

const createTimeSlots = (
  flexCal: FlexCalSummary,
  flexCalOpenings: Opening[],
  externalEvents: ExternalCalendarEventSummary[],
  participantEvents: ExternalCalendarEventSummary[] | undefined,
  externalCalendars: ExternalCalendar[] | undefined,
  reservations: Reservation[],
  filters: Filters,
  timeZone: TimeZone,
  clickedEvent: EventApi | undefined,
  selectedLocation: SelectedLocation,
  user: UserProfile | undefined
): Event[] => {
  //Initialize empty events array
  const events: Event[] = [];
  const {
    filterByMeetingLength,
    filterByPhysicalLocation,
    filterByVirtualLocation,
    getDurationInMinutes,
  } = useCalendarFilters(filters);
  //Buffer for which client does not allowe bookings in advance
  const bookingBuffer: number | undefined = flexCal?.firstAvailableTimeInHours;
  //Loop through all opening objects for this Flex Cal (Scheduling Link)
  flexCalOpenings.forEach((opening) => {
    //Create a luxon Duration object for the time-span of this opening
    const openingDuration = DateTime.fromISO(opening.endDate).diff(
      DateTime.fromISO(opening.startDate),
      'minutes'
    );

    //Loop through all duration objects for the this opening
    opening.durations?.forEach((duration) => {
      //Used for filtering by meeting length. Add duration to meeting lengths array if not already present.
      const durationAsMin = getDurationInMinutes(duration);
      if (filterByMeetingLength(durationAsMin, filters)) return;
      //Boolean - if physical location filter condition is met
      const filteredPhysical = filterByPhysicalLocation(
        opening.physicalLocations,
        filters,
        selectedLocation
      );
      //Boolean - if virtual location filter condition is met
      const filteredVirtual = filterByVirtualLocation(
        opening.virtualLocations,
        filters,
        selectedLocation
      );
      //Escape from this opening if both location filer booleans are true (if none of the locations are selected)
      if (filteredPhysical && filteredVirtual) return;

      //Convert C# TimeSpan to Luxon Duration
      const slotDuration = Duration.fromISOTime(duration);
      //Calculate the number of timeslots for this opening by dividing the opnening duration by the timeslot duration
      const numTimeSlots = openingDuration.toMillis() / slotDuration.toMillis();
      //Loop through through timeslots and create FullCalendar Event objects for each
      let previousEndTime = DateTime.fromISO(opening.startDate);
      for (let i = 0; i < numTimeSlots; i++) {
        //set timeslot end time
        const newEndTime = previousEndTime.plus(slotDuration);
        //Filter out events that overlap with reservations
        const isReserved = opening.reservations.some((reservation) => {
          const reservationStart = DateTime.fromISO(
            reservation.startTime
          ).toMillis();
          const reservationEnd = DateTime.fromISO(
            reservation.endTime
          ).toMillis();
          return (
            previousEndTime.toMillis() < reservationEnd &&
            newEndTime.toMillis() > reservationStart
          );
        });
        //Timeslot times set with correct timeZone
        const eventStart = previousEndTime.setZone(timeZone.tzdbId);
        const eventEnd = newEndTime.setZone(timeZone.tzdbId);
        //Find event that was selected by particpant. Done by checking aligment with time ranges and matching opening id
        const isClickedEvent =
          opening.openingId === clickedEvent?.extendedProps.openingId &&
          i === clickedEvent?.extendedProps.timeSlotIdx &&
          duration === clickedEvent?.extendedProps.duration;
        //Populate FC event objects
        const timeSlot: Event = {
          start: flexCal.evergreen
            ? handleDST(eventStart, timeZone).toString()
            : eventStart.toString(),
          end: flexCal.evergreen
            ? handleDST(eventEnd, timeZone).toString()
            : eventEnd.toString(),
          backgroundColor: isClickedEvent ? '#86E3A8' : '#FEFEFF',
          borderColor: '#40978F',
          textColor: '#000000',
          id: isClickedEvent ? 'clicked' : undefined,
          extendedProps: {
            physicalLocations: opening.physicalLocations,
            virtualLocations: opening.virtualLocations,
            openingId: opening.openingId,
            timeSlotIdx: i,
            duration: duration,
            type: 'subscriber',
          },
        };
        //If this timeslot is the earliest in the day set it as the new earliest timeslot
        if (previousEndTime.hour < earliestTimeSlot)
          earliestTimeSlot = previousEndTime.hour;
        //Filter out events that are inside bookingBuffer
        if (
          !isReserved &&
          previousEndTime.toMillis() >=
            DateTime.now().plus({ hours: bookingBuffer }).toMillis() &&
          !isBookedExternally(eventStart, eventEnd, externalEvents) &&
          !isBookedInternally(eventStart, eventEnd, reservations)
        )
          events.push(timeSlot);
        //Set next timeslot range
        previousEndTime = newEndTime;
      }
    });
  });
  participantEvents?.forEach((event) => {
    const eventStart = DateTime.fromISO(event.startDate).setZone(
      timeZone.tzdbId
    );
    const eventEnd = DateTime.fromISO(event.endDate).setZone(timeZone.tzdbId);

    const timeSlot: Event = {
      start: handleDST(eventStart, timeZone).toString(),
      end: handleDST(eventEnd, timeZone).toString(),
      backgroundColor: getSlotColor(user?.sub, event, externalCalendars),
      borderColor: getSlotColor(user?.sub, event, externalCalendars),
      textColor: getSlotColor(user?.sub, event, externalCalendars),
      extendedProps: {
        type: 'participant',
      },
      overlap: true,
    };
    events.push(timeSlot);
  });
  return events;
};

const getSlotPositioning = (
  duration: number,
  durations: number[],
  eventParent: HTMLElement
) => {
  if (durations[0] === duration) {
    return eventParent.classList.add(`${styles.first}`);
  }
  if (durations.length === 3) {
    return eventParent.classList.add(`${styles.oneHalf}`);
  }
  if (durations.length === 4) {
    if (durations[1] === duration) {
      return eventParent.classList.add(`${styles.oneThird}`);
    } else {
      return eventParent.classList.add(`${styles.twoThirds}`);
    }
  }
  if (durations.length === 5) {
    if (durations[1] === duration) {
      return eventParent.classList.add(`${styles.oneFourth}`);
    }
    if (durations[2] === duration) {
      return eventParent.classList.add(`${styles.oneHalf}`);
    } else {
      return eventParent.classList.add(`${styles.threeFourths}`);
    }
  }
  if (durations.length === 6) {
    if (durations[1] === duration) {
      return eventParent.classList.add(`${styles.oneFifth}`);
    }
    if (durations[2] === duration) {
      return eventParent.classList.add(`${styles.twoFifths}`);
    }
    if (durations[3] === duration) {
      return eventParent.classList.add(`${styles.threeFifths}`);
    } else {
      return eventParent.classList.add(`${styles.fourFifths}`);
    }
  }
};

const getSlotColor = (
  userName: string | undefined | null,
  event: ExternalCalendarEventSummary | undefined,
  externalCalendars: ExternalCalendar[] | undefined
) => {
  if (externalCalendars && externalCalendars?.length > 0 && event) {
    const provider = externalCalendars.find(
      (ec) => ec.name === event.calendarName
    )?.provider;
    if (provider === 'Google') return '#F9A825';
    if (provider === 'Free/Busy') return '#87E3A8';
    if (provider === 'Microsoft') return '#0078D4';
  } else {
    if (!userName) return '#87E3A8';
    if (userName.includes('waad')) return '#0078D4';
    if (userName.includes('google')) return '#F9A825';
  }
  return '#87E3A8';
};
